// Style
import mainStyle from "../mainStyle.module.scss";

// Packages
import React, { useState, useMemo, useEffect } from "react";
import {
  Box,
  Button,
  Typography,
  Divider,
  InputAdornment,
  IconButton,
  Collapse,
} from "@mui/material";
import { set } from "lodash";
import { useTheme } from "@mui/material/styles";

// Components
import { Loading } from 'components/Loading';
import { SolidButton } from "components/buttons/SolidButton";
import { OutlineButton } from "components/buttons/OutlineButton";
import ModalContainer from "components/ModalContainer";
import { MenuSelect } from "components/Select/menuSelect";
import { StyledBox } from "../components/StyledBox";
import { StyledInput } from "../components/StyledInput";

// Icons
import { ReactComponent as CloseIcon } from "assets/images/icons/icn_close.svg";
import { ReactComponent as ShowIcon } from "assets/images/icons/icn_input_show_hover.svg";
import { ReactComponent as HideIcon } from "assets/images/icons/icn_input_hide.svg";
import { ReactComponent as ChevronSolidIcon } from "assets/images/icons/icn_chevron_solid.svg";

// Local const
import {
  defaultActions,
  defaultAuthTypeList,
  defaultAccountingDefinition,
  defaultAccountingList,
  defaultIsCollapse,
  defaultIsShowPassword,
} from "../const";

// Local Utils
import {
  getInitSelectedRadius,
  getIsAuthServerInvalid,
  getIsPortInvalid,
  getIsFormInvalid,
} from '../helper/createAndUpdate';

const CreateAndEditModal = ({
  selectedRadius,
  setSelectedRadius,
  actions,
  setActions,
  showEditGeneral,
}) => {
  const theme = useTheme();

  const isCreateMode = useMemo(() => !selectedRadius?.id, [selectedRadius]);
  const [selectedModalRadius, setSelectedModalRadius] = useState(getInitSelectedRadius({ selectedRadius }));

  const [authTypeMap, setAuthTypeMap] = useState(structuredClone(defaultAuthTypeList));
  const [accountingMap, setAccountingMap] = useState(structuredClone(defaultAccountingList));

  const [isCollapse, setIsCollapse] = useState(structuredClone(defaultIsCollapse));
  const [isShowPassword, setIsShowPassword] = useState(structuredClone(defaultIsShowPassword));

  const isPrimaryAccountingDisabled = useMemo(() => selectedModalRadius?.primary?.accountingType === defaultAccountingDefinition.disable.value, [selectedModalRadius?.primary?.accountingType]);
  const isLoading = useMemo(() => actions.isCreateAndSave || actions.isEditAndSave, [actions.isCreateAndSave, actions.isEditAndSave]);

  const onCreateOrApply = () => {
    setSelectedRadius(structuredClone(selectedModalRadius));

    if (isCreateMode) setActions({ ...defaultActions, isCreate: true, isCreateAndSave: true });
    else if (!isCreateMode) setActions({ ...defaultActions, isEdit: true, isEditAndSave: true });
  };

  const onCancel = () => {
    setActions({ ...defaultActions, isCreate: false });
  };

  const onUpdateValue = ({
    key, // "name", "primary.xxx", "primary.accounting.xxx"
    value,
  }) => {
    setSelectedModalRadius(prev => {
      const newState = structuredClone(prev);
      set(newState, key, value);

      return newState;
    })
  }

  const onToggleIsShowPassword = ({
    key
  }) => {
    setIsShowPassword((prev) => {
      return {
        ...prev,
        [key]: !prev[key]
      }
    });
  };

  useEffect(() => {
    if (isPrimaryAccountingDisabled) {
      setAccountingMap(prevMap => {
        return {
          ...prevMap,
          secondary: prevMap.secondary.map(accounting => {
            return {
              ...accounting,
              isSelected: accounting.value === defaultAccountingDefinition.disable.value
            }
          })
        }
      })

      onUpdateValue({
        key: "secondary.accountingType",
        value: defaultAccountingDefinition.disable.value
      });
    }
  }, [isPrimaryAccountingDisabled]);

  useEffect(() => {
    return () => {
      setSelectedRadius(null);
      setSelectedModalRadius(null);
    }
  }, [])

  return (
    <ModalContainer display={actions?.isCreate || actions?.isEdit} modalWidth="750px">
      {isLoading && <Loading />}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >

        <Typography
          sx={{
            mb: "30px",
            ml: "20px",
            color: theme.palette.primary.main,
            fontFamily: theme.typography.fontFamily,
            fontSize: theme.typography.customFontSize[5],
            fontWeight: theme.typography.customFontWeight[3],
          }}
        >
          {isCreateMode ? "Create" : "Edit"} RADIUS Profile
        </Typography>

        <Button
          onClick={onCancel}
          sx={{
            marginRight: "-10px",
            marginTop: "-28px",
            backgroundColor: theme.palette.primary.transparent,
            boxShadow: "none",
            "&:hover": {
              backgroundColor: theme.palette.primary.transparent,
            },
          }}
        >
          <CloseIcon />
        </Button>

      </Box>

      <Box sx={{ ml: "20px" }}>

        {/* Radius Name */}
        <Box sx={{ marginBottom: 3 }}>
          <StyledBox sx={{ mt: "0px", ml: "32px", mb: "0px", gap: 5 }}>
            <Typography className={mainStyle["collapse-typography"]} sx={{ minWidth: 0 }}>Profile Name</Typography>
            <StyledInput
              sx={{ width: "355px" }}
              type="text"
              disabled={!showEditGeneral}
              value={selectedModalRadius?.name || ""}
              isInvalid={selectedModalRadius?.name === ""}
              onChange={e => {
                onUpdateValue({
                  key: "name",
                  value: e.target.value
                })
              }}
            />
          </StyledBox>
        </Box>

        {/* Primary Remote RADIUS Server */}
        <Box sx={{ mt: "20px" }}>
          <Box>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                paddingTop: "7px",
                cursor: "pointer",
                gap: "8px",
              }}
              onClick={() => setIsCollapse(prev => ({ ...prev, primary: !prev.primary }))}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  transform: isCollapse?.primary ? "rotate(90deg)" : "rotate(0deg)",
                }}
              >
                <ChevronSolidIcon />
              </Box>
              <Typography
                sx={{
                  color: theme.palette.primary.greyPrimary,
                  fontSize: theme.typography.customFontSize[2],
                  fontWeight: theme.typography.customFontWeight[1],
                }}
              >
                Primary Remote RADIUS Server
              </Typography>
            </Box>

            <Collapse in={isCollapse.primary}>

              {/* Primary Authenticate Server */}
              <StyledBox>
                <Typography className={mainStyle["collapse-typography"]}>
                  Authenticate Server
                </Typography>
                <StyledInput
                  type="text"
                  placeholder="e.g. 10.90.90.90"
                  disabled={!showEditGeneral}
                  value={selectedModalRadius?.primary?.authenticateServer || ""}
                  isInvalid={
                    selectedModalRadius?.primary?.authenticateServer != null &&
                    getIsAuthServerInvalid({ authServer: selectedModalRadius?.primary?.authenticateServer })
                  }
                  onChange={e => {
                    onUpdateValue({
                      key: "primary.authenticateServer",
                      value: e.target.value
                    })
                  }}
                />
              </StyledBox>

              {/* Primary Port */}
              <StyledBox>
                <Typography className={mainStyle["collapse-typography"]}>
                  Port
                </Typography>
                <StyledInput
                  type="number"
                  placeholder="1-65535"
                  disabled={!showEditGeneral}
                  value={selectedModalRadius?.primary?.port || ""}
                  isInvalid={
                    selectedModalRadius?.primary?.port != null &&
                    getIsPortInvalid({ port: selectedModalRadius?.primary?.port })
                  }
                  onChange={e => {
                    onUpdateValue({
                      key: "primary.port",
                      value: e.target.value
                    })
                  }}
                />
              </StyledBox>

              {/* Primary Secret */}
              <StyledBox>
                <Typography className={mainStyle["collapse-typography"]}>
                  Secret
                </Typography>
                <StyledInput
                  type={isShowPassword?.primary ? "text" : "password"}
                  disabled={!showEditGeneral}
                  value={selectedModalRadius?.primary?.secret || ""}
                  isInvalid={selectedModalRadius?.primary?.secret === ""}
                  onChange={e => {
                    onUpdateValue({
                      key: "primary.secret",
                      value: e.target.value
                    })
                  }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => onToggleIsShowPassword({ key: "primary" })}
                          edge="end"
                          sx={{
                            padding: 1,
                            "&:hover": {
                              backgroundColor:
                                theme.palette.primary.transparent,
                            },
                            "&.Mui-focusVisible": {
                              backgroundColor:
                                theme.palette.primary.transparent,
                            },
                          }}
                        >
                          {isShowPassword?.primary ? <ShowIcon /> : <HideIcon />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </StyledBox>

              {/* Primary Authentication Type */}
              <StyledBox>
                <Typography className={mainStyle["collapse-typography"]}>
                  Authentication Type
                </Typography>
                <MenuSelect
                  width="310px"
                  disabled={!showEditGeneral}
                  value={selectedModalRadius?.primary?.authenticationType ?? ""}
                  onChange={opt => {
                    setAuthTypeMap(prevMap => {
                      return {
                        ...prevMap,
                        primary: prevMap.primary.map(authType => {
                          return {
                            ...authType,
                            isSelected: authType.value === opt.value
                          }
                        })
                      }
                    })

                    onUpdateValue({
                      key: "primary.authenticationType",
                      value: opt.value
                    });

                  }}
                  options={authTypeMap?.primary}
                />
              </StyledBox>

              {/* Primary Accounting */}
              <StyledBox>
                <Typography className={mainStyle["collapse-typography"]}>
                  Accounting
                </Typography>
                <MenuSelect
                  width="310px"
                  disabled={!showEditGeneral}
                  value={selectedModalRadius?.primary?.accountingType ?? ""}
                  onChange={opt => {
                    setAccountingMap(prevMap => {
                      return {
                        ...prevMap,
                        primary: prevMap.primary.map(accounting => {
                          return {
                            ...accounting,
                            isSelected: accounting.value === opt.value
                          }
                        })
                      }
                    })

                    onUpdateValue({
                      key: "primary.accountingType",
                      value: opt.value
                    });
                  }}
                  options={accountingMap?.primary}
                />
              </StyledBox>
            </Collapse>

          </Box>

        </Box>

        {/* Primary Accounting server */}
        {
          selectedModalRadius?.primary?.accountingType === defaultAccountingDefinition.independent.value &&
          <Box sx={{ display: "flex", gap: 5, mt: "20px" }}>
            <Box>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  paddingTop: "15px",
                  cursor: "pointer",
                  gap: "8px",
                }}
                onClick={() => setIsCollapse(prev => ({ ...prev, primaryAccounting: !prev.primaryAccounting }))}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    transform: isCollapse?.primaryAccounting ? "rotate(90deg)" : "rotate(0deg)",
                  }}
                >
                  <ChevronSolidIcon />
                </Box>
                <Typography
                  sx={{
                    color: theme.palette.primary.greyPrimary,
                    fontSize: theme.typography.customFontSize[2],
                    fontWeight: theme.typography.customFontWeight[1],
                  }}
                >
                  Accounting Server
                </Typography>
              </Box>

              <Collapse in={isCollapse.primaryAccounting}>

                {/* Primary Accounting Authenticate Server */}
                <StyledBox>
                  <Typography className={mainStyle["collapse-typography"]}>
                    Accounting Server
                  </Typography>
                  <StyledInput
                    type="text"
                    placeholder="e.g. 10.90.90.90"
                    disabled={!showEditGeneral}
                    value={selectedModalRadius?.primary?.accounting?.accountingServer || ""}
                    isInvalid={
                      selectedModalRadius?.primary?.accounting?.accountingServer != null &&
                      getIsAuthServerInvalid({ authServer: selectedModalRadius?.primary?.accounting?.accountingServer })
                    }
                    onChange={e => {
                      onUpdateValue({
                        key: "primary.accounting.accountingServer",
                        value: e.target.value
                      });
                    }}
                  />
                </StyledBox>

                {/* Primary Accounting Port */}
                <StyledBox>
                  <Typography className={mainStyle["collapse-typography"]}>
                    Port
                  </Typography>
                  <StyledInput
                    type="number"
                    placeholder="1-65535"
                    disabled={!showEditGeneral}
                    value={selectedModalRadius?.primary?.accounting?.port || ""}
                    isInvalid={
                      selectedModalRadius?.primary?.accounting?.port != null &&
                      getIsPortInvalid({ port: selectedModalRadius?.primary?.accounting?.port })
                    }
                    onChange={e => {
                      onUpdateValue({
                        key: "primary.accounting.port",
                        value: e.target.value
                      });
                    }}
                  />
                </StyledBox>

                {/* Primary Accounting Secret */}
                <StyledBox>
                  <Typography className={mainStyle["collapse-typography"]}>
                    Secret
                  </Typography>
                  <StyledInput
                    type={isShowPassword?.primaryAccounting ? "text" : "password"}
                    disabled={!showEditGeneral}
                    value={selectedModalRadius?.primary?.accounting?.secret || ""}
                    isInvalid={selectedModalRadius?.primary?.accounting?.secret === ""}
                    onChange={e => {
                      onUpdateValue({
                        key: "primary.accounting.secret",
                        value: e.target.value
                      });
                    }}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={() => onToggleIsShowPassword({ key: "primaryAccounting" })}
                            edge="end"
                            sx={{
                              padding: 1,
                              "&:hover": {
                                backgroundColor:
                                  theme.palette.primary.transparent,
                              },
                              "&.Mui-focusVisible": {
                                backgroundColor:
                                  theme.palette.primary.transparent,
                              },
                            }}
                          >
                            {isShowPassword?.primaryAccounting ? <ShowIcon /> : <HideIcon />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                </StyledBox>

                {/* Primary Accounting Authentication Type */}
                <StyledBox>
                  <Typography className={mainStyle["collapse-typography"]}>
                    Authentication Type
                  </Typography>
                  <MenuSelect
                    width="310px"
                    disabled={!showEditGeneral}
                    value={selectedModalRadius?.primary?.accounting?.authenticationType ?? ""}
                    onChange={opt => {
                      setAuthTypeMap(prevMap => {
                        return {
                          ...prevMap,
                          primaryAccounting: prevMap.primaryAccounting.map(authType => {
                            return {
                              ...authType,
                              isSelected: authType.value === opt.value
                            }
                          })
                        }
                      })

                      onUpdateValue({
                        key: "primary.accounting.authenticationType",
                        value: opt.value
                      });
                    }}
                    options={authTypeMap?.primaryAccounting}
                  />
                </StyledBox>
              </Collapse>
            </Box>
            <Divider />
          </Box>
        }

        <Divider sx={{ margin: "40px 0px 20px" }} />

        {/* Secondary Remote RADIUS Server (Optional) */}
        <Box sx={{ mt: "20px" }}>
          <Box>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                paddingTop: "7px",
                cursor: "pointer",
                gap: "8px",
              }}
              onClick={() => setIsCollapse(prev => ({ ...prev, secondary: !prev.secondary }))}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  transform: isCollapse?.secondary ? "rotate(90deg)" : "rotate(0deg)",
                }}
              >
                <ChevronSolidIcon />
              </Box>
              <Typography
                sx={{
                  color: theme.palette.primary.greyPrimary,
                  fontSize: theme.typography.customFontSize[2],
                  fontWeight: theme.typography.customFontWeight[1],
                }}
              >
                Secondary Remote RADIUS Server (Optional)
              </Typography>
            </Box>

            <Collapse in={isCollapse.secondary}>

              {/* Secondary Authenticate Server */}
              <StyledBox>
                <Typography className={mainStyle["collapse-typography"]}>
                  Authenticate Server
                </Typography>
                <StyledInput
                  type="text"
                  placeholder="e.g. 10.90.90.90"
                  disabled={!showEditGeneral}
                  value={selectedModalRadius?.secondary?.authenticateServer || ""}
                  isInvalid={
                    selectedModalRadius?.secondary?.authenticateServer &&
                    getIsAuthServerInvalid({ authServer: selectedModalRadius?.secondary?.authenticateServer })
                  }
                  onChange={e => {
                    onUpdateValue({
                      key: "secondary.authenticateServer",
                      value: e.target.value
                    });
                  }}
                />
              </StyledBox>

              {/* Secondary Port */}
              <StyledBox>
                <Typography className={mainStyle["collapse-typography"]}>
                  Port
                </Typography>
                <StyledInput
                  type="number"
                  placeholder="1-65535"
                  disabled={!showEditGeneral}
                  value={selectedModalRadius?.secondary?.port || ""}
                  isInvalid={
                    selectedModalRadius?.secondary?.port &&
                    getIsPortInvalid({ port: selectedModalRadius?.secondary?.port })
                  }
                  onChange={e => {
                    onUpdateValue({
                      key: "secondary.port",
                      value: e.target.value
                    });
                  }}
                />
              </StyledBox>

              {/* Secondary Secret */}
              <StyledBox>
                <Typography className={mainStyle["collapse-typography"]}>
                  Secret
                </Typography>
                <StyledInput
                  type={isShowPassword?.secondary ? "text" : "password"}
                  disabled={!showEditGeneral}
                  value={selectedModalRadius?.secondary?.secret || ""}
                  onChange={e => {
                    onUpdateValue({
                      key: "secondary.secret",
                      value: e.target.value
                    });
                  }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => onToggleIsShowPassword({ key: "secondary" })}
                          edge="end"
                          sx={{
                            padding: 1,
                            "&:hover": {
                              backgroundColor:
                                theme.palette.primary.transparent,
                            },
                            "&.Mui-focusVisible": {
                              backgroundColor:
                                theme.palette.primary.transparent,
                            },
                          }}
                        >
                          {isShowPassword?.secondary ? <ShowIcon /> : <HideIcon />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </StyledBox>

              {/* Secondary Authentication Type */}
              <StyledBox>
                <Typography className={mainStyle["collapse-typography"]}>
                  Authentication Type
                </Typography>
                <MenuSelect
                  width="310px"
                  disabled={!showEditGeneral}
                  value={selectedModalRadius?.secondary?.authenticationType ?? ""}
                  onChange={opt => {
                    setAuthTypeMap(prevMap => {
                      return {
                        ...prevMap,
                        secondary: prevMap.secondary.map(authType => {
                          return {
                            ...authType,
                            isSelected: authType.value === opt.value
                          }
                        })
                      }
                    })

                    onUpdateValue({
                      key: "secondary.authenticationType",
                      value: opt.value
                    });
                  }}
                  options={authTypeMap?.secondary}
                />
              </StyledBox>

              {/* Secondary Accounting */}
              <StyledBox>
                <Typography className={mainStyle["collapse-typography"]}>
                  Accounting
                </Typography>
                <MenuSelect
                  width="310px"
                  disabled={!showEditGeneral || isPrimaryAccountingDisabled}
                  value={selectedModalRadius?.secondary?.accountingType ?? ""}
                  onChange={opt => {
                    setAccountingMap(prevMap => {
                      return {
                        ...prevMap,
                        secondary: prevMap.secondary.map(accounting => {
                          return {
                            ...accounting,
                            isSelected: accounting.value === opt.value
                          }
                        })
                      }
                    })

                    onUpdateValue({
                      key: "secondary.accountingType",
                      value: opt.value
                    });
                  }}
                  options={accountingMap?.secondary}
                />
              </StyledBox>
            </Collapse>

          </Box>

        </Box>

        {/* Secondary Accounting server */}
        {
          selectedModalRadius?.secondary?.accountingType === defaultAccountingDefinition.independent.value &&
          <Box sx={{ display: "flex", gap: 5, mt: "20px" }}>
            <Box>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  paddingTop: "15px",
                  cursor: "pointer",
                  gap: "8px",
                }}
                onClick={() => setIsCollapse(prev => ({ ...prev, secondaryAccounting: !prev.secondaryAccounting }))}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    transform: isCollapse?.secondaryAccounting ? "rotate(90deg)" : "rotate(0deg)",
                  }}
                >
                  <ChevronSolidIcon />
                </Box>
                <Typography
                  sx={{
                    color: theme.palette.primary.greyPrimary,
                    fontSize: theme.typography.customFontSize[2],
                    fontWeight: theme.typography.customFontWeight[1],
                  }}
                >
                  Accounting Server
                </Typography>
              </Box>

              <Collapse in={isCollapse.secondaryAccounting}>

                {/* Secondary Accounting Server */}
                <StyledBox>
                  <Typography className={mainStyle["collapse-typography"]}>
                    Accounting Server
                  </Typography>
                  <StyledInput
                    type="text"
                    placeholder="e.g. 10.90.90.90"
                    disabled={!showEditGeneral}
                    value={selectedModalRadius?.secondary?.accounting?.accountingServer || ""}
                    isInvalid={
                      selectedModalRadius?.secondary?.accounting?.accountingServer &&
                      getIsAuthServerInvalid({ authServer: selectedModalRadius?.secondary?.accounting?.accountingServer })
                    }
                    onChange={e => {
                      onUpdateValue({
                        key: "secondary.accounting.accountingServer",
                        value: e.target.value
                      });
                    }}
                  />
                </StyledBox>

                {/* Secondary Accounting Port */}
                <StyledBox>
                  <Typography className={mainStyle["collapse-typography"]}>
                    Port
                  </Typography>
                  <StyledInput
                    type="number"
                    placeholder="1-65535"
                    disabled={!showEditGeneral}
                    value={selectedModalRadius?.secondary?.accounting?.port || ""}
                    isInvalid={
                      selectedModalRadius?.secondary?.accounting?.port &&
                      getIsPortInvalid({ port: selectedModalRadius?.secondary?.accounting?.port })
                    }
                    onChange={e => {
                      onUpdateValue({
                        key: "secondary.accounting.port",
                        value: e.target.value
                      });
                    }}
                  />
                </StyledBox>

                {/* Secondary Accounting Secret */}
                <StyledBox>
                  <Typography className={mainStyle["collapse-typography"]}>
                    Secret
                  </Typography>
                  <StyledInput
                    type={isShowPassword?.secondaryAccounting ? "text" : "password"}
                    disabled={!showEditGeneral}
                    value={selectedModalRadius?.secondary?.accounting?.secret || ""}
                    onChange={e => {
                      onUpdateValue({
                        key: "secondary.accounting.secret",
                        value: e.target.value
                      });
                    }}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={() => onToggleIsShowPassword({ key: "secondaryAccounting" })}
                            edge="end"
                            sx={{
                              padding: 1,
                              "&:hover": {
                                backgroundColor:
                                  theme.palette.primary.transparent,
                              },
                              "&.Mui-focusVisible": {
                                backgroundColor:
                                  theme.palette.primary.transparent,
                              },
                            }}
                          >
                            {isShowPassword?.secondaryAccounting ? <ShowIcon /> : <HideIcon />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                </StyledBox>

                {/* Secondary Accounting Authentication Type */}
                <StyledBox>
                  <Typography className={mainStyle["collapse-typography"]}>
                    Authentication Type
                  </Typography>
                  <MenuSelect
                    width="310px"
                    disabled={!showEditGeneral}
                    value={selectedModalRadius?.secondary?.accounting?.authenticationType ?? ""}
                    onChange={opt => {
                      setAuthTypeMap(prevMap => {
                        return {
                          ...prevMap,
                          secondaryAccounting: prevMap.secondaryAccounting.map(authType => {
                            return {
                              ...authType,
                              isSelected: authType.value === opt.value
                            }
                          })
                        }
                      })

                      onUpdateValue({
                        key: "secondary.accounting.authenticationType",
                        value: opt.value
                      });
                    }}
                    options={authTypeMap?.secondaryAccounting}
                  />
                </StyledBox>

              </Collapse>

            </Box>

          </Box>
        }

      </Box>

      <Box
        sx={{
          position: "sticky",
          bottom: "0px",
          display: "flex",
          pt: "30px",
          pb: "20px",
          justifyContent: "center",
          gap: "20px",
          width: "100%",
          backgroundColor: "primary.white",
        }}
      >
        <OutlineButton
          sx={{ marginRight: "20px" }}
          width="100px"
          height="32px"
          onClick={onCancel}
        >
          Cancel
        </OutlineButton>
        <SolidButton
          width="100px"
          height="32px"
          disabled={getIsFormInvalid({ selectedRadius: selectedModalRadius })}
          onClick={onCreateOrApply}
        >
          {isCreateMode ? "Create" : "Apply"}
        </SolidButton>
      </Box>
    </ModalContainer>
  );
};

export default CreateAndEditModal;
